import type { CreateTravelerDto, TravelerResponseDto, UpdateTravelerDto } from '@bandido/shared/dtos';
import type { RuteroDeclarablePassengerField } from '@bandido/shared/types';

import type { RuteroPassengerPayload } from './queries';
import { isValidE164 } from './phoneNumber';
import { identityDocumentPatch, reconcilePersistedIdentityDocument, transferDocumentFromTraveler } from './transferIdentityDocument';

const canonicalFields = [
  'title',
  'firstName',
  'lastName',
  'birthDate',
  'email',
  'phone',
  'nationality',
  'countryOfResidence',
] as const;

type CanonicalField = (typeof canonicalFields)[number];
type OptionalCanonicalField = Exclude<CanonicalField, 'firstName' | 'lastName'>;

const optionalCanonicalFields = new Set<OptionalCanonicalField>([
  'title',
  'birthDate',
  'email',
  'phone',
  'nationality',
  'countryOfResidence',
]);

const bookingSpecificContactFields = [
  'flightNumber',
  'returnFlightNumber',
  'driverNotes',
] as const;

const present = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const validEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const validCalendarDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

const isoCountry = (value: string | undefined): string | undefined => {
  const normalized = value?.trim().toUpperCase();
  return normalized?.length === 2 ? normalized : undefined;
};

/**
 * Lo reutilizable de una fila ad-hoc. Los datos operativos del servicio no
 * entran nunca al perfil. Transfers conserva el tipo elegido en el draft;
 * los demás productos mantienen la semántica de pasaporte.
 */
export function travelerCreationInput(passenger: RuteroPassengerPayload): CreateTravelerDto | null {
  const firstName = passenger.firstName.trim();
  const lastName = passenger.lastName.trim();
  if (!firstName || !lastName) return null;

  const title = passenger.title?.trim();
  const birthDate = passenger.birthDate?.trim();
  const email = passenger.email?.trim();
  const phone = passenger.phone?.trim();
  const passportNumber = passenger.passportNumber?.trim();
  const passportExpiry = passenger.passportExpiry?.trim();
  const nationality = isoCountry(passenger.nationality);
  const countryOfResidence = isoCountry(passenger.countryOfResidence);

  return {
    firstName,
    lastName,
    ...(title ? { title } : {}),
    ...(birthDate && validCalendarDate(birthDate) ? { birthDate } : {}),
    ...(email && validEmail(email) ? { email } : {}),
    ...(phone && isValidE164(phone) ? { phone } : {}),
    ...(nationality ? { nationality } : {}),
    ...(countryOfResidence ? { countryOfResidence } : {}),
    ...(passportNumber
      ? {
        documents: [
          {
            type: passenger.identityDocument?.type ?? 'passport',
            number: passportNumber,
            ...((!passenger.identityDocument || passenger.identityDocument.type === 'passport') && passportExpiry && validCalendarDate(passportExpiry) ? { expiresOn: passportExpiry } : {}),
          },
        ],
      }
      : {}),
  };
}

/** Conserva el snapshot exacto y agrega solamente su identidad CRM. */
export function passengerWithCreatedTraveler(
  passenger: RuteroPassengerPayload,
  traveler: TravelerResponseDto,
): RuteroPassengerPayload {
  return {
    ...reconcilePersistedIdentityDocument(passenger, passenger, traveler),
    travelerId: traveler.id,
    travelerSourceId: traveler.id,
  };
}

/**
 * Enlaza sobre el draft vivo para no reinstalar valores capturados antes del
 * await. Una respuesta de un draft reemplazado conserva el alta CRM, pero no
 * puede adjudicarse a otra fila que casualmente ocupa la misma posición.
 */
export function linkCreatedTravelerToPassengerDraft(
  passengers: RuteroPassengerPayload[],
  passengerIndex: number,
  traveler: TravelerResponseDto,
  expectedDraft: number,
  currentDraft: number,
): RuteroPassengerPayload[] {
  if (expectedDraft !== currentDraft) return passengers;
  return passengers.map((passenger, index) =>
    index === passengerIndex
      ? passengerWithCreatedTraveler(passenger, traveler)
      : passenger);
}

export interface TravelerCreationRequest {
  passengerIndex: number;
  input: CreateTravelerDto;
}

export interface ScopedTravelerCreationInput {
  passengerDraft: number;
  input: CreateTravelerDto;
}

/** La identidad idempotente y SU payload forman una sola operación durable. */
export function stableTravelerCreationInput(
  pending: ScopedTravelerCreationInput | undefined,
  current: CreateTravelerDto,
  passengerDraft: number,
  createRequestId: () => string,
): ScopedTravelerCreationInput {
  return pending?.passengerDraft === passengerDraft ? pending : {
    passengerDraft,
    input: {
      ...current,
      creationRequestId: createRequestId(),
    },
  };
}

/** Una operación vieja no puede mutar el estado de un draft reemplazado. */
export function travelerCreationDraftIsCurrent(
  expectedDraft: number,
  currentDraft: number,
): boolean {
  return expectedDraft === currentDraft;
}

/**
 * Ejecuta altas secuencialmente mientras siga vivo el draft que las originó.
 * Si cambia durante un request, conserva el alta CRM ya hecha pero no publica
 * su resultado ni empieza las altas restantes.
 */
export async function runTravelerCreationsForDraft<T, R>(
  items: readonly T[],
  expectedDraft: number,
  currentDraft: () => number,
  submit: (item: T) => Promise<R>,
  commit: (item: T, result: R) => void,
): Promise<boolean> {
  for (const item of items) {
    if (!travelerCreationDraftIsCurrent(expectedDraft, currentDraft())) {
      return false;
    }
    const result = await submit(item);
    if (!travelerCreationDraftIsCurrent(expectedDraft, currentDraft())) {
      return false;
    }
    commit(item, result);
  }
  return true;
}

/**
 * Sin respuesta, timeout, rate limit o error de servidor no prueban que el
 * INSERT no ocurrió. Los demás 4xx sí permiten corregir y abrir otra operación.
 */
export function travelerCreationFailureIsAmbiguous(
  status: number | undefined,
): boolean {
  return status === undefined || status >= 500 || [408, 425, 429].includes(status);
}

/** Un refetch de metadata del mismo caso no inaugura otro draft de viajeros. */
export function bookingCaseDraftScopeChanged(
  previousCaseId: number | null | undefined,
  nextCaseId: number | null,
): boolean {
  return previousCaseId !== nextCaseId;
}

/**
 * Sólo el caso creado dentro de este booking propone altas por defecto. Un
 * perfil ya vinculado o un opt-out nunca vuelve a entrar en un retry.
 */
export function pendingTravelerCreations(
  passengers: readonly RuteroPassengerPayload[],
  preferences: Readonly<Record<number, boolean>>,
  enabled: boolean,
): TravelerCreationRequest[] {
  if (!enabled) return [];
  return passengers.flatMap((passenger, passengerIndex) => {
    if (passenger.travelerId || preferences[passengerIndex] === false) return [];
    const input = travelerCreationInput(passenger);
    return input ? [{ passengerIndex, input }] : [];
  });
}

/**
 * Copia solo datos canónicos del perfil. Tipo tarifario, habitación, contacto
 * y datos propios del servicio permanecen atados a esta reserva.
 */
export function passengerFromTraveler(
  passenger: RuteroPassengerPayload,
  traveler: TravelerResponseDto,
  options: {
    visibleFields: ReadonlySet<RuteroDeclarablePassengerField>;
    identityDocuments?: boolean;
    /** Campos cuya autoridad pasa al perfil elegido, incluso si allí faltan. */
    authoritativeFields?: ReadonlySet<OptionalCanonicalField>;
  },
): RuteroPassengerPayload {
  const previousTravelerId =
    passenger.travelerSourceId ?? passenger.travelerId;
  const next = {
    ...passenger,
    travelerId: traveler.id,
    travelerSourceId: traveler.id,
  };
  if (previousTravelerId && previousTravelerId !== traveler.id) {
    for (const field of canonicalFields) {
      delete (next as Partial<RuteroPassengerPayload>)[field];
    }
    delete next.passportNumber;
    delete next.passportExpiry;
    delete next.identityDocument;
  }
  for (const field of canonicalFields) {
    if (
      field !== 'firstName' &&
      field !== 'lastName' &&
      !options.visibleFields.has(field)
    ) {
      continue;
    }
    const value = traveler[field];
    if (present(value)) next[field] = value;
    else if (
      field !== 'firstName' &&
      field !== 'lastName' &&
      options.authoritativeFields?.has(field)
    ) delete next[field];
  }
  if (
    options.visibleFields.has('passportNumber') ||
    options.visibleFields.has('passportExpiry')
  ) {
    if (options.identityDocuments && options.visibleFields.has('passportNumber')) {
      return transferDocumentFromTraveler(next, traveler);
    }
    const passport = traveler.documents.find(
      (document) => document.type.toLowerCase() === 'passport',
    );
    if (options.visibleFields.has('passportNumber') && passport?.number) {
      next.passportNumber = passport.number;
    }
    if (options.visibleFields.has('passportExpiry') && passport?.expiresOn) {
      next.passportExpiry = passport.expiresOn;
    }
  }
  return next;
}

export function travelerAlreadyAssigned(
  travelerId: string,
  passengers: readonly RuteroPassengerPayload[],
  passengerIndex: number,
): boolean {
  return passengers.some((passenger, index) => index !== passengerIndex && passenger.travelerId === travelerId);
}

export function reconcileTravelerAssignments(
  passengers: RuteroPassengerPayload[],
  rosterTravelerIds: ReadonlySet<string>,
): RuteroPassengerPayload[] {
  let changed = false;
  const reconciled = passengers.map((passenger) => {
    if (!passenger.travelerId || rosterTravelerIds.has(passenger.travelerId)) {
      return passenger;
    }
    changed = true;
    return { ...passenger, travelerId: undefined };
  });
  return changed ? reconciled : passengers;
}

/** El contacto es una decisión del booking, nunca una mutación del perfil. */
export function withBookingContact(
  passengers: readonly RuteroPassengerPayload[],
  passengerIndex: number,
  options?: {
    travelers: readonly TravelerResponseDto[];
    identityDocuments?: boolean;
    contactFields: ReadonlySet<RuteroDeclarablePassengerField>;
    visibleFields: (
      index: number,
      isContact: boolean,
    ) => ReadonlySet<RuteroDeclarablePassengerField>;
  },
): RuteroPassengerPayload[] {
  const previousContact = passengers[bookingContactPassengerIndex(passengers)];
  return passengers.map((passenger, index) => {
    const isContact = index === passengerIndex;
    const next = {
      ...passenger,
      ...(isContact
        ? { isContactPerson: true }
        : { isContactPerson: undefined }),
    };
    if (!options) return next;
    const visibleFields = options.visibleFields(index, isContact);
    const traveler = options.travelers.find(
      (candidate) => candidate.id === next.travelerId,
    );
    for (const field of optionalCanonicalFields) {
      if (!visibleFields.has(field)) {
        delete next[field];
      } else if (next[field] === undefined) {
        const value = traveler?.[field];
        if (present(value)) next[field] = value;
      }
    }
    const passport = traveler?.documents.find(
      (document) => document.type.toLowerCase() === 'passport',
    );
    if (!visibleFields.has('passportNumber')) {
      delete next.passportNumber;
      delete next.identityDocument;
    } else if (options.identityDocuments && next.passportNumber === undefined && traveler) {
      Object.assign(next, transferDocumentFromTraveler(next, traveler));
    } else if (next.passportNumber === undefined && passport?.number) {
      next.passportNumber = passport.number;
    }
    if (!visibleFields.has('passportExpiry')) {
      delete next.passportExpiry;
    } else if (!options.identityDocuments && next.passportExpiry === undefined && passport?.expiresOn) {
      next.passportExpiry = passport.expiresOn;
    }
    for (const field of bookingSpecificContactFields) {
      if (!visibleFields.has(field)) {
        delete next[field];
      } else if (isContact && options.contactFields.has(field)) {
        const value = previousContact?.[field];
        if (value === undefined) delete next[field];
        else next[field] = value;
      }
    }
    return next;
  });
}

export function bookingContactPassengerIndex(passengers: readonly RuteroPassengerPayload[]): number {
  const selected = passengers.findIndex((passenger) => passenger.isContactPerson === true);
  return selected >= 0 ? selected : 0;
}

export function bookingContactPassenger(
  passengers: readonly RuteroPassengerPayload[],
): RuteroPassengerPayload | undefined {
  return passengers[bookingContactPassengerIndex(passengers)];
}

/**
 * Detecta cambios canónicos para ofrecer una acción explícita. Omitir esa
 * acción deja el cambio únicamente en el snapshot del booking.
 */
export function canonicalTravelerPatch(
  passenger: RuteroPassengerPayload,
  traveler: TravelerResponseDto,
): UpdateTravelerDto | null {
  const patch: UpdateTravelerDto = {};
  for (const field of canonicalFields) {
    const value = passenger[field];
    if (present(value)) {
      const normalized = value.trim();
      if (normalized !== (traveler[field] ?? '').trim()) {
        (patch as Record<CanonicalField, string>)[field] = normalized;
      }
    } else if (
      field !== 'firstName' &&
      field !== 'lastName' &&
      optionalCanonicalFields.has(field) &&
      typeof value === 'string' &&
      present(traveler[field])
    ) {
      (patch as Record<OptionalCanonicalField, string | null>)[
        field as OptionalCanonicalField
      ] = null;
    }
  }
  if (passenger.identityDocument) {
    const documents = identityDocumentPatch(passenger, traveler);
    if (documents) patch.documents = documents;
    return Object.keys(patch).length ? patch : null;
  }
  const passport = traveler.documents.find(
    (document) => document.type.toLowerCase() === 'passport',
  );
  if (
    passport &&
    typeof passenger.passportNumber === 'string' &&
    !present(passenger.passportNumber)
  ) {
    patch.documents = traveler.documents
      .filter((document) => document.id !== passport.id)
      .map((document) => ({
        type: document.type,
        ...(document.issuingCountry
          ? { issuingCountry: document.issuingCountry }
          : {}),
        number: document.number,
        ...(document.expiresOn ? { expiresOn: document.expiresOn } : {}),
      }));
  } else {
    const passengerPassportNumber = present(passenger.passportNumber)
      ? passenger.passportNumber.trim()
      : undefined;
    const passportNumberChanged = Boolean(
      passengerPassportNumber &&
      passengerPassportNumber !== passport?.number.trim(),
    );
    const passportExpiryChanged = Boolean(
      passport &&
      typeof passenger.passportExpiry === 'string' &&
      passenger.passportExpiry.trim() !== (passport.expiresOn ?? ''),
    );
    if (!passportNumberChanged && !passportExpiryChanged) {
      return Object.keys(patch).length ? patch : null;
    }
    const nextExpiry =
      typeof passenger.passportExpiry === 'string'
        ? passenger.passportExpiry.trim()
        : passport?.expiresOn ?? '';
    const updatedPassport = {
      type: passport?.type ?? 'passport',
      ...(passport?.issuingCountry
        ? { issuingCountry: passport.issuingCountry }
        : traveler.nationality
          ? { issuingCountry: traveler.nationality }
          : {}),
      number: passengerPassportNumber ?? passport?.number ?? '',
      ...(nextExpiry
        ? { expiresOn: nextExpiry }
        : {}),
    };
    patch.documents = passport
      ? traveler.documents.map((document) =>
        document.id === passport.id
          ? updatedPassport
          : {
            type: document.type,
            ...(document.issuingCountry
              ? { issuingCountry: document.issuingCountry }
              : {}),
            number: document.number,
            ...(document.expiresOn ? { expiresOn: document.expiresOn } : {}),
          })
      : [
        ...traveler.documents.map((document) => ({
          type: document.type,
          ...(document.issuingCountry
            ? { issuingCountry: document.issuingCountry }
            : {}),
          number: document.number,
          ...(document.expiresOn ? { expiresOn: document.expiresOn } : {}),
        })),
        updatedPassport,
      ];
  }
  return Object.keys(patch).length ? patch : null;
}

/**
 * Una corrección segura arranca marcada solo cuando completa información que
 * el perfil no tenía (o un teléfono histórico que el booking no puede usar).
 * Reemplazar un dato válido sigue requiriendo una decisión explícita.
 */
export function travelerPatchShouldPersistByDefault(
  patch: UpdateTravelerDto,
  traveler: TravelerResponseDto,
): boolean {
  const fields = Object.keys(patch) as Array<keyof UpdateTravelerDto>;
  if (!fields.length) return false;

  return fields.every((field) => {
    if (field === 'documents') {
      const currentPassport = traveler.documents.find(
        (document) => document.type.toLowerCase() === 'passport',
      );
      const nextPassport = patch.documents?.find(
        (document) => document.type.toLowerCase() === 'passport',
      );
      return !currentPassport && Boolean(nextPassport);
    }
    const current = traveler[field];
    if (field === 'phone') {
      return !present(current as string | null) ||
        !isValidE164(current as string);
    }
    return !present(current as string | null);
  });
}

export interface TravelerProfileUpdate {
  travelerId: string;
  input: UpdateTravelerDto;
}

/** Snapshot de los PATCH autorizados justo antes del primer hold. */
export function travelerProfileUpdates(
  passengers: readonly RuteroPassengerPayload[],
  travelers: readonly TravelerResponseDto[],
  preferences: Readonly<Record<string, boolean>>,
): TravelerProfileUpdate[] {
  const travelerById = new Map(
    travelers.map((traveler) => [traveler.id, traveler]),
  );
  return passengers.flatMap((passenger) => {
    if (!passenger.travelerId) return [];
    const traveler = travelerById.get(passenger.travelerId);
    if (!traveler) return [];
    const input = canonicalTravelerPatch(passenger, traveler);
    if (!input) return [];
    const selected = preferences[traveler.id] ??
      travelerPatchShouldPersistByDefault(input, traveler);
    return selected ? [{ travelerId: traveler.id, input }] : [];
  });
}
